import {
  CalendarTask,
  CalendarTasksGroup,
  CommonUtils,
  Time,
} from '@infodata-group/inf-commons';
import { AgendaDetailedOptions } from './agenda-detailed-options';
import { AgendaDetailedSubCell } from './agenda-detailed-sub-cell';

export interface PositionedTask {
  task: CalendarTask;
  start: number;
  end: number;
  column?: number;
  columnsCount?: number;
}
export class AgendaDetailedCell {
  public resource: CalendarTasksGroup;
  public date: Date;
  public subCells: AgendaDetailedSubCell[] = [];
  public step: number;
  public viewTasks: { style: any; task: CalendarTask }[] = [];
  public options: AgendaDetailedOptions;
  public lastChild = false;

  constructor(
    resource: CalendarTasksGroup,
    date: Date,
    options: AgendaDetailedOptions,
  ) {
    this.resource = resource;
    this.date = date;
    this.options = options;
    this.generateSubCells();
  }

  get height(): number {
    let height = 0;
    this.subCells.forEach((subCell) => {
      height += subCell.height;
    });
    return height;
  }

  private generateSubCells() {
    if (this.options.daySection) {
      this.generateDaySectionsSubCells();
      return;
    }
    this.generateHoursSectionsSubCells();
  }

  private generateHoursSectionsSubCells() {
    const toMinutes = (t: Time): number => t.hours * 60 + t.minutes;
    const toTime = (minutes: number): Time => ({
      hours: Math.floor(minutes / 60),
      minutes: minutes % 60,
    });
    let current = toMinutes(this.options.morningStart);

    let end = toMinutes(this.options.afternoonEnd);
    const step = this.options.hoursSection === 1 ? 60 : 30;
    if (step === 30) {
      while (current < end) {
        const next = current + step;
        this.subCells.push(
          new AgendaDetailedSubCell(
            toTime(current),
            toTime(next),
            this.resource,
            this.date,
          ),
        );
        current = next;
      }
    } else {
      if (this.options.morningStart.minutes === 30) {
        this.subCells.push(
          new AgendaDetailedSubCell(
            toTime(current),
            {
              hours: toTime(current).hours,
              minutes: 30,
            },
            this.resource,
            this.date,
          ),
        );
        current += 30;
      }
      if (this.options.afternoonEnd.minutes === 30) {
        end -= 30;
      }
      while (current < end) {
        this.subCells.push(
          new AgendaDetailedSubCell(
            toTime(current),
            toTime(current + step),
            this.resource,
            this.date,
          ),
        );
        current += step;
      }
      if (this.options.afternoonEnd.minutes === 30) {
        this.subCells.push(
          new AgendaDetailedSubCell(
            toTime(current),
            {
              hours: toTime(current).hours,
              minutes: 30,
            },
            this.resource,
            this.date,
          ),
        );
      }
    }
  }

  private generateDaySectionsSubCells() {
    if (this.options.daySection === 1) {
      const cell = new AgendaDetailedSubCell(
        this.options.morningStart,
        this.options.afternoonEnd,
        this.resource,
        this.date,
        this.options.sectionHeight * 2,
      );
      this.subCells.push(cell);
      return;
    }
    this.subCells.push(
      new AgendaDetailedSubCell(
        this.options.morningStart,
        this.options.morningEnd,
        this.resource,
        this.date,
        this.options.sectionHeight,
      ),
    );
    this.subCells.push(
      new AgendaDetailedSubCell(
        this.options.afternoonStart,
        this.options.afternoonEnd,
        this.resource,
        this.date,
        this.options.sectionHeight,
      ),
    );
  }

  private getMinutesDiff(time1: Time, time2: Time): number {
    const startTotalMinutes = time1.hours * 60 + time1.minutes;
    const endTotalMinutes = time2.hours * 60 + time2.minutes;
    return endTotalMinutes - startTotalMinutes;
  }

  private getTimeInMinutes(time: Time): number {
    return time.hours * 60 + time.minutes;
  }

  private getPositionedTasks(
    resourceTasks: CalendarTask[],
    startDayInMinutes: number,
    endDayInMinutes: number,
  ): PositionedTask[] {
    return resourceTasks
      .map((task) => {
        let startMinutes = this.getMinutesDiff(this.options.morningStart, {
          hours: task.dateStart.hour(),
          minutes: task.dateStart.minute(),
        });
        if (startMinutes < 0) {
          startMinutes = 0;
        }
        let durationMinutes = this.getTimeInMinutes(
          CommonUtils.convertStrToTime(task.getDuration()),
        );
        if (startDayInMinutes + durationMinutes > endDayInMinutes) {
          durationMinutes = endDayInMinutes - startDayInMinutes;
        }
        const taskTimeStartInMinutes = this.getTimeInMinutes({
          hours: task.dateStart.get('hours'),
          minutes: task.dateStart.get('minutes'),
        });
        if (taskTimeStartInMinutes >= endDayInMinutes) {
          return null;
        }
        const taskTimeEndInMinutes = this.getTimeInMinutes({
          hours: task.dateEnd.get('hours'),
          minutes: task.dateEnd.get('minutes'),
        });
        if (taskTimeEndInMinutes <= startDayInMinutes) {
          return null;
        }
        return {
          task,
          start: startMinutes,
          end: startMinutes + durationMinutes,
        };
      })
      .filter((t) => t !== null) as PositionedTask[];
  }

  private getPositionedTasksByDaySection(
    resourceTasks: CalendarTask[],
  ): PositionedTask[] {
    return resourceTasks.map((task) => {
      const startMinutes = this.getTimeInMinutes({
        hours: task.dateStart.hour(),
        minutes: task.dateStart.minute(),
      });
      const endMinutes = this.getTimeInMinutes({
        hours: task.dateEnd.hour(),
        minutes: task.dateEnd.minute(),
      });
      return {
        task,
        start: startMinutes,
        end: endMinutes,
      };
    });
  }

  setTasksByHourSection(resourceTasks: CalendarTask[]) {
    const pixelPerMinute = this.options.hoursSection === 1 ? 2 : 1;
    const startDayInMinutes = this.getTimeInMinutes(this.options.morningStart);
    const endDayInMinutes = this.getTimeInMinutes(this.options.afternoonEnd);
    const positionedTasks = this.getPositionedTasks(
      resourceTasks,
      startDayInMinutes,
      endDayInMinutes,
    );
    this.computeColumns(positionedTasks);
    for (const t of positionedTasks) {
      let top = t.start * pixelPerMinute;
      let height = (t.end - t.start) * pixelPerMinute;
      if (
        top + height >
        (endDayInMinutes - startDayInMinutes) * pixelPerMinute
      ) {
        height = (endDayInMinutes - startDayInMinutes) * pixelPerMinute - top;
      }
      if (this.options.hoursSection === 1) {
        top /= 4;
        height /= 4;
      }
      const widthPercent = 100 / (t.columnsCount ?? 1);
      const leftPercent = (t.column ?? 0) * widthPercent;
      const marginRight = 10;
      const widthWithMargin = `calc(${widthPercent}% - ${marginRight}px)`;

      this.viewTasks.push({
        style: {
          top: `${top}px`,
          height: `${height}px`,
          width: widthWithMargin,
          left: `${leftPercent}%`,
          position: 'absolute',
          marginRight: `${marginRight}px`,
          borderColor: t.task.borderColor ?? t.task.backgroundColor,
        },
        task: t.task,
      });
    }
  }

  setTasksByDaySection(resourceTasks: CalendarTask[]) {
    const endMorningInMinutes = this.getTimeInMinutes(this.options.morningEnd);
    const startAfternoonInMinutes = this.getTimeInMinutes(
      this.options.afternoonStart,
    );
    const positionedTasks = this.getPositionedTasksByDaySection(resourceTasks);
    this.computeColumnsByDaySection(
      positionedTasks,
      endMorningInMinutes,
      startAfternoonInMinutes,
    );
    for (const t of positionedTasks) {
      const widthPercent = 100 / (t.columnsCount ?? 1);
      const leftPercent = (t.column ?? 0) * widthPercent;
      const marginRight = 10;
      const widthWithMargin = `calc(${widthPercent}% - ${marginRight}px)`;
      let top = 0;
      let height = this.options.sectionHeight * 2;
      if (this.options.daySection === 2) {
        const taskStartInMinutes = this.getTimeInMinutes({
          hours: t.task.dateStart.get('hours'),
          minutes: t.task.dateStart.get('minutes'),
        });
        const taskEndInMinutes = this.getTimeInMinutes({
          hours: t.task.dateEnd.get('hours'),
          minutes: t.task.dateEnd.get('minutes'),
        });
        if (taskStartInMinutes >= endMorningInMinutes) {
          top = this.options.sectionHeight;
          height /= 2;
        } else if (taskEndInMinutes <= startAfternoonInMinutes) {
          height /= 2;
        }
      }
      this.viewTasks.push({
        style: {
          top: `${top}px`,
          height: `${height}px`,
          width: widthWithMargin,
          left: `${leftPercent}%`,
          position: 'absolute',
          marginRight: `${marginRight}px`,
          borderColor: t.task.borderColor ?? t.task.backgroundColor,
        },
        task: t.task,
      });
    }
  }

  setTasks(resourceTasks: CalendarTask[]) {
    if (this.options.hoursSection) {
      this.setTasksByHourSection(resourceTasks);
      return;
    }
    this.setTasksByDaySection(resourceTasks);
  }

  private computeColumnsByDaySection(
    tasks: PositionedTask[],
    endMorningInMinutes: number,
    startAfternoonInMinutes: number,
  ) {
    tasks.sort((a, b) => a.start - b.start);
    if (this.options.daySection === 1) {
      tasks.forEach((t, i) => {
        t.column = i;
        t.columnsCount = tasks.length;
      });
    } else {
      const allDayTasks = this.getAllDayTasks(
        tasks,
        endMorningInMinutes,
        startAfternoonInMinutes,
      );
      const morningTasks = this.getMorningTasks(tasks, startAfternoonInMinutes);
      const afternoonTasks = this.getAfternoonTasks(tasks, endMorningInMinutes);
      const columnCount =
        allDayTasks.length +
        Math.max(morningTasks.length, afternoonTasks.length);
      allDayTasks.forEach((t, i) => {
        t.column = i;
        t.columnsCount = columnCount;
      });
      morningTasks.forEach((t, i) => {
        t.column = allDayTasks.length + i;
        t.columnsCount = columnCount;
      });
      afternoonTasks.forEach((t, i) => {
        t.column = allDayTasks.length + i;
        t.columnsCount = columnCount;
      });
    }
  }

  private getMorningTasks(
    tasks: PositionedTask[],
    startAfternoonInMinutes: number,
  ) {
    return tasks.filter((task) => {
      const taskEndInMinutes = this.getTimeInMinutes({
        hours: task.task.dateEnd.get('hours'),
        minutes: task.task.dateEnd.get('minutes'),
      });
      return taskEndInMinutes <= startAfternoonInMinutes;
    });
  }

  private getAfternoonTasks(
    tasks: PositionedTask[],
    endMorningInMinutes: number,
  ) {
    return tasks.filter((task) => {
      const taskStartInMinutes = this.getTimeInMinutes({
        hours: task.task.dateStart.get('hours'),
        minutes: task.task.dateStart.get('minutes'),
      });
      return taskStartInMinutes >= endMorningInMinutes;
    });
  }

  private getAllDayTasks(
    tasks: PositionedTask[],
    endMorningInMinutes: number,
    startAfternoonInMinutes: number,
  ) {
    return tasks.filter((task) => {
      const taskStartInMinutes = this.getTimeInMinutes({
        hours: task.task.dateStart.get('hours'),
        minutes: task.task.dateStart.get('minutes'),
      });
      const taskEndInMinutes = this.getTimeInMinutes({
        hours: task.task.dateEnd.get('hours'),
        minutes: task.task.dateEnd.get('minutes'),
      });
      return (
        taskStartInMinutes < endMorningInMinutes &&
        taskEndInMinutes > startAfternoonInMinutes
      );
    });
  }

  private computeColumns(tasks: PositionedTask[]) {
    tasks.sort((a, b) => a.start - b.start);

    const groups: PositionedTask[][] = [];
    let currentGroup: PositionedTask[] = [];

    for (const task of tasks) {
      if (
        currentGroup.length === 0 ||
        task.start < Math.max(...currentGroup.map((t) => t.end))
      ) {
        currentGroup.push(task);
      } else {
        groups.push(currentGroup);
        currentGroup = [task];
      }
    }
    if (currentGroup.length) {
      groups.push(currentGroup);
    }

    for (const group of groups) {
      const columns: PositionedTask[][] = [];

      for (const task of group) {
        let placed = false;
        for (let i = 0; i < columns.length; i++) {
          if (!columns[i].some((t) => t.end > task.start)) {
            columns[i].push(task);
            task.column = i;
            placed = true;
            break;
          }
        }
        if (!placed) {
          columns.push([task]);
          task.column = columns.length - 1;
        }
      }

      const totalColumns = columns.length;
      group.forEach((t) => (t.columnsCount = totalColumns));
    }
  }
}
